import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { pick } from '../../util/common';
import { acceptStartNowAfterPay, initiatePrivileged, transitionPrivileged } from '../../util/api';
import { isStartNowBooking } from '../../util/sessionPricing';
import {
  pickRecoverableStartNowOrder,
  START_NOW_RECOVER_TRANSITIONS,
} from '../../util/startNowCheckout';
import { denormalisedResponseEntities } from '../../util/data';
import { isTooManyRequestsError, storableError } from '../../util/errors';
import {
  CHECKOUT_PAY_WAIT_MS,
  mapIdentityRequiredWhenBothVerified,
  shouldRetryIdentityRequiredAsFetch,
  withCheckoutPayTimeout,
} from '../../util/checkoutIdentityRetry';
import { markClientRateLimited } from '../../util/sharetribeRateLimit';
import { runWriteOnce } from '../../util/writeOnceOn429';
import * as log from '../../util/log';
import { setCurrentUserHasOrders, fetchCurrentUser } from '../../ducks/user.duck';

const withSessionRetry = (dispatch, work) =>
  work().catch(e => {
    if (e?.status === 401) {
      return dispatch(fetchCurrentUser({ enforce: true })).then(() => work());
    }
    throw e;
  });

// ================ Async thunks ================ //

////////////////////
// Initiate Order //
////////////////////
const checkoutPartiesFromState = getState => {
  const state = typeof getState === 'function' ? getState() : {};
  return {
    currentUser: state?.user?.currentUser,
    listing: state?.CheckoutPage?.listing,
  };
};

const initiateOrderPayloadCreator = (
  { orderParams, processAlias, transactionId, transitionName, isPrivilegedTransition },
  { dispatch, extra: sdk, rejectWithValue, getState }
) => {
  // If we already have a transaction ID, we should transition, not initiate.
  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    quantity,
    bookingDates,
    billingAddress,
    sessionFormat: sessionFormatParam,
    priceVariantName: priceVariantNameParam,
    firstSessionFree,
    declineFree,
    expectFree: expectFreeIgnoredOnInitiate,
    swapId,
    ...otherOrderParams
  } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server (privileged line items)
  const sessionFormat = sessionFormatParam || orderParams.protectedData?.sessionFormat;
  const priceVariantName = priceVariantNameParam;
  const orderData = {
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(priceVariantName ? { priceVariantName } : {}),
    ...(sessionFormat ? { sessionFormat } : {}),
    ...(billingAddress ? { billingAddress } : {}),
    ...(otherOrderParams.protectedData?.startNow === true || orderParams.startNow === true
      ? { startNow: true }
      : {}),
    // First Session Free (server re-checks eligibility; never trusted for pricing).
    ...(firstSessionFree === true ? { firstSessionFree: true } : {}),
    ...(declineFree === true ? { declineFree: true } : {}),
    ...(typeof swapId === 'string' && swapId ? { swapId } : {}),
  };

  // Marketplace API rejects unknown top-level params (sessionFormat, billingAddress).
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(setCurrentUserHasOrders());
    return order;
  };

  const handleError = e => {
    if (isTooManyRequestsError(e)) {
      markClientRateLimited();
    }
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      endpoint: isTransition ? 'transition' : 'initiate',
      status: e.status || e.sharetribeStatus || null,
      sharetribeCodes: (e.apiErrors || e.data?.errors || [])
        .map(item => item?.code)
        .filter(Boolean),
      ...transactionIdMaybe,
      listingId: orderParams.listingId?.uuid || orderParams.listingId,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
      statusText: e.statusText,
    });
    return rejectWithValue(storableError(e));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return withSessionRetry(dispatch, () =>
      runWriteOnce(() =>
        transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      )
    )
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return runWriteOnce(() => sdk.transactions.transition(bodyParams, queryParams))
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    const runPrivilegedInitiate = () =>
      withSessionRetry(dispatch, () =>
        runWriteOnce(() =>
          initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
        )
      );
    return runPrivilegedInitiate()
      .then(handleSuccess)
      .catch(e => {
        const { currentUser, listing } = checkoutPartiesFromState(getState);
        if (!shouldRetryIdentityRequiredAsFetch(e, currentUser, listing)) {
          return handleError(e);
        }
        return runPrivilegedInitiate()
          .then(handleSuccess)
          .catch(retryErr => {
            const parties = checkoutPartiesFromState(getState);
            return handleError(
              mapIdentityRequiredWhenBothVerified(retryErr, parties.currentUser, parties.listing)
            );
          });
      });
  } else {
    // initiate non-privileged
    return runWriteOnce(() => sdk.transactions.initiate(bodyParams, queryParams))
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const initiateOrderThunk = createAsyncThunk(
  'CheckoutPage/initiateOrder',
  initiateOrderPayloadCreator
);
// Backward compatible wrapper function for initiateOrder
export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => dispatch => {
  return dispatch(
    initiateOrderThunk({
      orderParams,
      processAlias,
      transactionId,
      transitionName,
      isPrivilegedTransition,
    })
  ).unwrap();
};

/////////////////////
// Confirm Payment //
/////////////////////
const confirmPaymentPayloadCreator = (
  { transactionId, transitionName, transitionParams = {} },
  { extra: sdk, rejectWithValue }
) => {
  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: transitionParams,
  };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const waitForStartNowAccept = async order => {
    if (!isStartNowBooking(null, order)) {
      // Instant booking (Oct 19 2026): book a scheduled time right after the hold.
      // The server answers 409 when instant booking is off, and the request flow stays.
      try {
        const result = await acceptStartNowAfterPay(order?.id?.uuid || order?.id);
        const last = result?.data?.lastTransition || result?.lastTransition;
        if (last === 'transition/accept' || last === 'transition/operator-accept') {
          return { ...order, attributes: { ...(order?.attributes || {}), lastTransition: last } };
        }
      } catch (e) {
        // Server sweep books it within ~2 minutes.
      }
      return order;
    }
    const accepted = new Set(['transition/accept', 'transition/operator-accept']);
    if (accepted.has(order?.attributes?.lastTransition)) {
      return order;
    }
    try {
      const result = await acceptStartNowAfterPay(order?.id?.uuid || order?.id);
      const last = result?.data?.lastTransition || result?.lastTransition;
      if (accepted.has(last)) {
        return {
          ...order,
          attributes: {
            ...(order?.attributes || {}),
            lastTransition: last,
          },
        };
      }
    } catch (e) {
      // Listener session may still accept via heartbeat / notification poll.
    }
    const deadline = Date.now() + 12000;
    let latest = order;
    while (Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, 1200));
      try {
        const shown = await sdk.transactions.show({
          id: transactionId,
          include: ['booking', 'provider'],
          expand: true,
        });
        latest = shown?.data?.data || latest;
        if (accepted.has(latest?.attributes?.lastTransition)) {
          return latest;
        }
      } catch (e) {
        // Keep waiting — the listener’s open tab accepts Start now.
      }
    }
    return latest;
  };

  return runWriteOnce(() => sdk.transactions.transition(bodyParams, queryParams))
    .then(response => waitForStartNowAccept(response.data.data))
    .catch(e => {
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'confirm-payment-failed', {
        endpoint: 'transition',
        status: e.status || e.sharetribeStatus || null,
        sharetribeCodes: (e.apiErrors || e.data?.errors || [])
          .map(item => item?.code)
          .filter(Boolean),
        ...transactionIdMaybe,
      });
      return rejectWithValue(storableError(e));
    });
};

export const confirmPaymentThunk = createAsyncThunk(
  'CheckoutPage/confirmPayment',
  confirmPaymentPayloadCreator
);
// Backward compatible wrapper function for confirmPayment
export const confirmPayment = (
  transactionId,
  transitionName,
  transitionParams = {}
) => dispatch => {
  return dispatch(
    confirmPaymentThunk({
      transactionId,
      transitionName,
      transitionParams,
    })
  ).unwrap();
};

//////////////////////
// Fetch Transaction //
//////////////////////
const fetchTransactionPayloadCreator = ({ id }, { extra: sdk, rejectWithValue }) => {
  return sdk.transactions
    .show({ id }, { expand: true })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      return entities[0];
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransactionThunk = createAsyncThunk(
  'CheckoutPage/fetchTransaction',
  fetchTransactionPayloadCreator
);

/**
 * Fetch an up-to-date transaction entity from Marketplace API.
 *
 * @param {UUID} id transaction id
 * @returns {Promise} transaction entity
 */
export const fetchTransaction = id => dispatch => {
  return dispatch(fetchTransactionThunk({ id })).unwrap();
};

///////////////////////////////
// Recover Start now checkout //
///////////////////////////////
const recoverStartNowCheckoutPayloadCreator = (
  { listingId, sessionFormat },
  { extra: sdk, rejectWithValue }
) => {
  return sdk.transactions
    .query({
      only: 'order',
      lastTransitions: START_NOW_RECOVER_TRANSITIONS,
      include: ['listing', 'booking', 'provider'],
      expand: true,
      perPage: 10,
    })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      const picked = pickRecoverableStartNowOrder(entities, { listingId, sessionFormat });
      if (!picked) {
        return null;
      }
      return sdk.transactions
        .show({
          id: picked.transaction.id,
          include: ['booking', 'provider'],
          expand: true,
        })
        .then(shown => {
          const [tx] = denormalisedResponseEntities(shown);
          return { kind: picked.kind, transaction: tx || picked.transaction };
        });
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const recoverStartNowCheckoutThunk = createAsyncThunk(
  'CheckoutPage/recoverStartNowCheckout',
  recoverStartNowCheckoutPayloadCreator
);

/**
 * Find this talker's in-flight or already-paid Start now order for the listing.
 *
 * @param {Object} listingId
 * @param {string} [sessionFormat]
 * @returns {Promise<{kind: string, transaction: Object}|null>}
 */
export const recoverStartNowCheckout = (listingId, sessionFormat) => dispatch => {
  return dispatch(recoverStartNowCheckoutThunk({ listingId, sessionFormat })).unwrap();
};

//////////////////////
// Initiate Inquiry //
//////////////////////

const initiateInquiryPayloadCreator = (
  { inquiryParams, processAlias, transitionName },
  { extra: sdk, rejectWithValue }
) => {
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: inquiryParams?.listingId?.uuid,
    });
    return rejectWithValue(storableError(error));
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      return transactionId;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const initiateInquiryThunk = createAsyncThunk(
  'CheckoutPage/initiateInquiry',
  initiateInquiryPayloadCreator
);
// Backward compatible wrapper function for initiateInquiryWithoutPayment
/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {Object} params
 * @param {Object} params.inquiryParams contains listingId and protectedData
 * @param {String} params.processAlias 'default-inquiry/release-1'
 * @param {String} params.transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (
  inquiryParams,
  processAlias,
  transitionName
) => dispatch => {
  return dispatch(
    initiateInquiryThunk({
      inquiryParams,
      processAlias,
      transitionName,
    })
  ).unwrap();
};

///////////////////////////
// Speculate Transaction //
///////////////////////////
/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */

const speculateTransactionPayloadCreator = (
  { orderParams, processAlias, transactionId, transitionName, isPrivilegedTransition },
  { dispatch, extra: sdk, rejectWithValue }
) => {
  // If we already have a transaction ID, we should transition, not initiate.
  const isTransition = !!transactionId;

  const {
    deliveryMethod,
    priceVariantName,
    sessionFormat,
    quantity,
    bookingDates,
    billingAddress,
    firstSessionFree: firstSessionFreeIgnored,
    declineFree,
    expectFree,
    swapId: swapIdSpec,
    ...otherOrderParams
  } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = {
    // First Session Free: the server quotes $0 when eligible unless the talker opted out.
    ...(declineFree === true ? { declineFree: true } : {}),
    // The panel showed "Free": refuse (409) instead of silently quoting the normal price.
    ...(expectFree === true && declineFree !== true ? { expectFree: true } : {}),
    ...(typeof swapIdSpec === 'string' && swapIdSpec ? { swapId: swapIdSpec } : {}),
    ...(deliveryMethod ? { deliveryMethod } : {}),
    ...(priceVariantName ? { priceVariantName } : {}),
    ...(sessionFormat ? { sessionFormat } : {}),
    ...(billingAddress ? { billingAddress } : {}),
    ...(otherOrderParams.protectedData?.startNow === true || orderParams.startNow === true
      ? { startNow: true }
      : {}),
  };

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    return tx;
  };

  const handleError = e => {
    if (isTooManyRequestsError(e)) {
      markClientRateLimited();
    }
    log.error(e, 'speculate-transaction-failed', {
      endpoint: isTransition ? 'speculate-transition' : 'speculate',
      status: e.status || e.sharetribeStatus || null,
      sharetribeCodes: (e.apiErrors || e.data?.errors || [])
        .map(item => item?.code)
        .filter(Boolean),
      listingId: transitionParams.listingId?.uuid || transitionParams.listingId,
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
      statusText: e.statusText,
    });
    return rejectWithValue(storableError(e));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return withSessionRetry(dispatch, () =>
      runWriteOnce(() =>
        transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      )
    )
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return runWriteOnce(() => sdk.transactions.transitionSpeculative(bodyParams, queryParams))
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return withSessionRetry(dispatch, () =>
      runWriteOnce(() =>
        initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      )
    )
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return runWriteOnce(() => sdk.transactions.initiateSpeculative(bodyParams, queryParams))
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const speculateTransactionThunk = createAsyncThunk(
  'CheckoutPage/speculateTransaction',
  speculateTransactionPayloadCreator
);
// Backward compatible wrapper function for speculateTransaction
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => dispatch => {
  return dispatch(
    speculateTransactionThunk({
      orderParams,
      processAlias,
      transactionId,
      transitionName,
      isPrivilegedTransition,
    })
  ).unwrap();
};

///////////////////////////
// Fetch Stripe Customer //
///////////////////////////
const stripeCustomerPayloadCreator = ({}, { dispatch, rejectWithValue }) => {
  const fetchCurrentUserOptions = {
    callParams: { include: ['stripeCustomer.defaultPaymentMethod'] },
    updateHasListings: false,
    updateNotifications: false,
    enforce: true,
  };

  return withCheckoutPayTimeout(
    dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
    CHECKOUT_PAY_WAIT_MS,
    'stripe_customer_timeout'
  )
    .then(response => {
      return response;
    })
    .catch(e => {
      if (isTooManyRequestsError(e)) {
        markClientRateLimited();
      }
      return rejectWithValue(storableError(e));
    });
};

export const stripeCustomerThunk = createAsyncThunk(
  'CheckoutPage/stripeCustomer',
  stripeCustomerPayloadCreator
);
// Backward compatible wrapper function for stripeCustomer
export const stripeCustomer = () => dispatch => {
  return dispatch(stripeCustomerThunk({})).unwrap();
};

// ================ Slice ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  stripeCustomerFetchError: null,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
};

const checkoutPageSlice = createSlice({
  name: 'CheckoutPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...pick(action.payload, Object.keys(initialState)) };
    },
  },
  extraReducers: builder => {
    builder
      // Initiate Order cases
      .addCase(initiateOrderThunk.pending, state => {
        state.initiateOrderError = null;
      })
      .addCase(initiateOrderThunk.fulfilled, (state, action) => {
        state.transaction = action.payload;
      })
      .addCase(initiateOrderThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.initiateOrderError = action.payload;
      })
      // Confirm Payment cases
      .addCase(confirmPaymentThunk.pending, state => {
        state.confirmPaymentError = null;
      })
      .addCase(confirmPaymentThunk.fulfilled, state => {
        // Payment confirmed successfully, no state change needed
      })
      .addCase(confirmPaymentThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.confirmPaymentError = action.payload;
      })
      // Speculate Transaction cases
      .addCase(speculateTransactionThunk.pending, state => {
        state.speculateTransactionInProgress = true;
        state.speculateTransactionError = null;
        // Keep the last good speculate. Tax re-price must not wipe the card form.
      })
      .addCase(speculateTransactionThunk.fulfilled, (state, action) => {
        state.speculateTransactionInProgress = false;
        state.speculateTransactionError = null;
        state.speculatedTransaction = action.payload;
      })
      .addCase(speculateTransactionThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.speculateTransactionInProgress = false;
        // A later tax-address retry must not hide checkout if we already have a quote.
        if (!state.speculatedTransaction) {
          state.speculateTransactionError = action.payload;
        }
      })
      // Stripe Customer cases
      .addCase(stripeCustomerThunk.pending, state => {
        state.stripeCustomerFetchError = null;
      })
      .addCase(stripeCustomerThunk.fulfilled, state => {
        state.stripeCustomerFetched = true;
      })
      .addCase(stripeCustomerThunk.rejected, (state, action) => {
        console.error(action.payload);
        // Still show the card form. A failed include must not leave Pay on a spinner.
        state.stripeCustomerFetched = true;
        state.stripeCustomerFetchError = action.payload;
      })
      // Initiate Inquiry cases
      .addCase(initiateInquiryThunk.pending, state => {
        state.initiateInquiryInProgress = true;
        state.initiateInquiryError = null;
      })
      .addCase(initiateInquiryThunk.fulfilled, state => {
        state.initiateInquiryInProgress = false;
      })
      .addCase(initiateInquiryThunk.rejected, (state, action) => {
        state.initiateInquiryInProgress = false;
        state.initiateInquiryError = action.payload;
      });
  },
});

// Export the action creators
export const { setInitialValues } = checkoutPageSlice.actions;

// Export the reducer
export default checkoutPageSlice.reducer;
